import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, onMounted, inject } from 'vue';
import { BannerModel, BannerStatus, EditBannerModel, IBannerEditManager, IBannerCreator, IBannerImageManager, UpdateBannerStatus } from '@truckdown/locations';
import validationError from '../../Validation/Error/Error.vue';
import dropzone from '../../Dropzone/Dropzone.vue';

import { BannerValidator } from '../Validation/BannerValidator';
import { ILookupService, ServiceModel } from '@truckdown/systems';
import { showMessage } from '../../Utilities/utilities';
import { CreateBannerModel } from '@truckdown/locations/Banners/CreateBannerModel';

//import ServicesForm from '../ListingServicesEdit/ServicesForm/ServicesForm.vue';
interface UploadFile {
    file: File;
    preview: string;
    status: 'pending' | 'success' | 'error';
    id: string
}
const CANADIAN_REGION_CODES: string[] = [ "AB", "BC", "MB", "NB",  "NL", "NS", "NT", "NU", "ON", "PE",  "QC",  "SK", "YT" ];

interface IData {
    modalContent: Ref<HTMLInputElement | null>,
    formStep: Ref<number>,
    isLoading: Ref<boolean>,
    errorMessage: Ref<string>,
    infoMessage: Ref<string>,
    bannerType: Ref<string>,
    editBannerModel: Ref<any>,
    originalEditModel: Ref<any>,
    bannerValidator: Ref<any>,
    selectableRegions: Ref<string[]>,
    selectableCountries: Ref<any>,
    imageUrl: Ref<string>,
    imageIdToRemove: Ref<string>,
    updateStatusModel: Ref<UpdateBannerStatus>
}

const getInitialData = function (props: any): IData {

    let createBannerModel = {
        status: BannerStatus.Inactive,
        accountId: props.accountId,
        imageId: "",
        websiteAddress: "",
        bannerServices: [],
        regionCode: "",
        countryCode: ""
    }
    const getEditModel = function (banner: any) {
        return {
            id: banner.id,
            status: banner.status,
            accountId: banner.accountId,
            imageId: banner.imageId,
            websiteAddress: banner.websiteAddress,
            bannerServices: banner.bannerServices,
            regionCode: banner.regionCode,
            countryCode: banner.countryCode,
        } as EditBannerModel;
    }

    return {
        modalContent: ref<HTMLInputElement | null>(null),
        errorMessage: ref<string>(""),
        infoMessage: ref<string>(""),
        formStep: ref<number>(props.editStep || 1),
        isLoading: ref<boolean>(false),
        bannerType: ref<string>(props.banner && props.banner.regionCode ? "regional" : "national"),
        originalEditModel: ref<any>(props.banner ? getEditModel(props.banner) : JSON.parse(JSON.stringify(createBannerModel))),
        editBannerModel: ref<any>(props.banner ? getEditModel(props.banner) : createBannerModel),
        bannerValidator: ref<any>({}),
        selectableRegions: ref<string[]>([]),
        selectableCountries: ref<any>([
            { code: "CA", name: "Canada" },
            { code: "US", name: "USA" },
        ]),
        imageUrl: ref<string>(props.banner?.imageId ? '/listings/banners/load-banner-image/' + props.banner.id : ""),
        imageIdToRemove: ref<string>(''),
        updateStatusModel: ref<UpdateBannerStatus>({
            bannerId: props.banner?.id || "",
            status: BannerStatus.Inactive
        })
    };
}


const getComputed = function (props: any, data: IData) {

    return {
        formTitle: computed(() => {
            return props.banner ? 'Edit Banner' : 'Create a Banner'
        }),
        buttonTitle: computed(() => {
            return props.banner ? 'Save Edits' : 'Create Banner'
        }),
        hasSubs: computed(() => {
            return props.banner.activeSubscriptions?.length > 0;
        }),
    };
}


const getMethods = function (props: any, data: IData, ctx: any) {
    let scrollToTop = function () {
        if (data.modalContent.value) {
            data.modalContent.value?.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const updateStatus = async function (banner: BannerModel) {
        data.updateStatusModel.value.status = banner.status;
        try {
            const manager = await context.getService<IBannerEditManager>('IBannerEditManager');
            let result = await manager.updateStatus(data.updateStatusModel.value);
            if (result && result.ok) {
                return banner;
            } else {
                return null;
            }
        } catch (error) {
            console.error('An error occured while changing banner status:', error);
        }
    }

    const deleteImage = async function (imageId: string) {
        const manager = await context.getService<IBannerImageManager>('IBannerImageManager');
        try {
            let result = await manager.removeImage(data.originalEditModel.value.id, imageId);
            //data.imageIdToRemove.value = "";
            //data.editBannerModel.value.imageId = "";
            //data.imageUrl.value = "";
        } catch (error) {
            //data.imageIdToRemove.value = data.editBannerModel.value.imageId;
            console.error('An error occured while removing the banner image:', error);
        }
    }
    
    return {
      
        selectRegion: function () {
            const normalized = data.editBannerModel.value.regionCode.toUpperCase().trim();
            if (CANADIAN_REGION_CODES.includes(normalized)) {
                data.editBannerModel.value.countryCode = "CA";
            } else {
                data.editBannerModel.value.countryCode = "US";
            }
        },

        selectType: function () {
           if (data.bannerType.value == 'national') {
              data.editBannerModel.value.regionCode = "";
           } else {
               data.editBannerModel.value.regionCode = "";
                data.editBannerModel.value.countryCode = "";
           }
        },

        isSelectedService: function (service: ServiceModel) {
            return data.editBannerModel.value.bannerServices.some((item: string) => item === service.code);
        },
        handleServiceSelect: function (service: ServiceModel) {
            const index = data.editBannerModel.value.bannerServices.findIndex((item: string) => item === service.code);
            if (index !== -1) {
                data.editBannerModel.value.bannerServices.splice(index, 1);
                data.errorMessage.value = "";
            } else {
                if (data.editBannerModel.value.bannerServices.length < 5) {
                    data.editBannerModel.value.bannerServices.push(service.code);
                    data.errorMessage.value = "";
                } else {
                    data.errorMessage.value = "You can select up to five service categories"
                }
            }
        },

        nextStep: async function (evt: Event): Promise<void> {
            data.errorMessage.value = "";
            if (data.formStep.value == 1) {
                if (data.bannerValidator.value.validate()) {
                    data.formStep.value = 2;
                    scrollToTop();
                }
            } 
        },
        previousStep: function (evt: Event): void {
            data.errorMessage.value = "";
            if (data.formStep.value > 1) {
                scrollToTop();
                data.formStep.value -= 1;
            }
        },
        goToStep: function (step: number) {
            data.errorMessage.value = "";
            const current = data.formStep.value;

            //// Leaving Step 1, going to Step 2
            if (current === 1 && step === 2) {
                if (!data.bannerValidator.value.validate()) return;
            }
           
            data.formStep.value = step;
            scrollToTop();
        },

        cancelEditBanner: async function ($event: Event) {
            //if (data.originalEditModel.value.imageId != data.editBannerModel.value.imageId) {
            //    await deleteImage(data.editBannerModel.value.imageId);
            //}
            ctx.emit('close-edit');
        },
        saveBanner: async function () {
            data.bannerValidator.value = new BannerValidator(data.editBannerModel.value);
            if (!data.isLoading.value && data.bannerValidator.value.validate() && data.editBannerModel.value.bannerServices.length > 0) {
                data.isLoading.value = true;
                try {
                    let result;
                    if (props.banner) {
                        const manager = await context.getService<IBannerEditManager>('IBannerEditManager');
                        if (data.originalEditModel.value.imageId != data.editBannerModel.value.imageId) {
                            await deleteImage(data.originalEditModel.value.imageId);
                        }
                        result = await manager.edit(data.editBannerModel.value as EditBannerModel);
                        if (result && result.data && data.editBannerModel.value.status != data.originalEditModel.value.status) {
                            result.data.status = data.editBannerModel.value.status;
                            await updateStatus(result.data);  
                        }
                        
                    } else {
                        const manager = await context.getService<IBannerCreator>('IBannerCreator');
                        result = await manager.createNewBanner(data.editBannerModel.value as CreateBannerModel);
                    }

                    if (result && result.ok) {
                        ctx.emit('submit-edit', result.data);
                    } else if (result && result.message) {
                        data.errorMessage.value = result.message;
                    }

                } catch (ex) {
                    scrollToTop();
                    data.errorMessage.value = "" + ex;
                }
                data.isLoading.value = false;
            }

            if (data.editBannerModel.value.bannerServices.length < 1) {
                data.errorMessage.value = "At least 1 service needs to be selected";
                data.formStep.value = 2;
            }

        },
        filesUploaded: function (files: UploadFile[]) {
            if (files[0].status != "error") {
                data.editBannerModel.value.imageId = files[0].id;
                data.imageUrl.value = files[0].preview;
                data.bannerValidator.value.validate("image"); 
            }
            data.isLoading.value = false;
        },
        startUpload: function () {
            data.isLoading.value = true;
        }

    };
}

const getRegions = async function (country: string | undefined, data: IData) {
    const lookupService = await context.getService<ILookupService>('ILookupService');
    let regionLookupResult = await lookupService.getRegions();
    if (country) {
        regionLookupResult.sort((a, b) =>
            (b.country === country ? 1 : 0) - (a.country === country ? 1 : 0)
        );
    }
    data.selectableRegions.value = Array.from(new Set(regionLookupResult.map(item => item.code)));
};

export default defineComponent({
    name: 'EditBanner',
    props: {
        isAdmin: Boolean,
        banner: {
            type: Object as () => BannerModel,
            required: false
        },
        allServices: {
            type: Array as () => ServiceModel[],
            default: []
        },
        accountId: {
            type: String,
            required: true
        }
    },
    components: {
        'error': validationError,
        'dropzone': dropzone
    },
    emits: ['close-edit', 'submit-edit'],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computed = getComputed(props, data);
        let methods = getMethods(props, data, ctx);

        onMounted(async () => {
            await getRegions("", data);
            data.bannerValidator.value = new BannerValidator(data.editBannerModel.value);
        });

        return {
            ...data,
            ...computed,
            ...methods,
            'BannerStatus': BannerStatus
        };
    }
});